import {
  ShiftManagementAreasViewPageDocument,
  ShiftManagementMainPageDocument,
  ShiftManagementShiftDropDocument,
  ShiftManagementShiftInteractorDropActionEnum,
} from 'generated/resources/graphql'
import type moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { dateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { trackAddShiftSubmitEvent } from 'modules/shift_management/lib/trackEvents'
import Shift from 'modules/shift_management/store/internal/models/Shift'

export interface CopyShiftProps {
  shift: Shift
  date: moment.Moment
  employeeId: string
  dropAction?: ShiftManagementShiftInteractorDropActionEnum
}

const useCopyShift = () => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view }] = useSchedule()
  const { refreshStats } = useRefreshStats()

  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const [dropShift] = useMutation(ShiftManagementShiftDropDocument, {
    extractorFn: (data) => data.shiftManagementMutations.dropShiftmanagementShift,
  })

  const copyShift = async ({ shift, date, employeeId, dropAction = 'copy' }: CopyShiftProps) => {
    const formattedDate = dateToString(date, 'backend')
    const result = await dropShift({
      variables: {
        id: shift.id,
        employeeId,
        companyId: currentCompanyId,
        date: formattedDate,
        dropAction,
      },
      // TODO: Actually update the cache properly
      refetchQueries: [ShiftManagementMainPageDocument, ShiftManagementAreasViewPageDocument],
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      if (handled.data.shift) {
        const newShift = Shift.buildFromGraphQL({
          shift: handled.data.shift,
          employeeFullName: handled.data.shift?.employee?.fullName || '',
          employeeAvatar: handled.data.shift?.employee?.avatar?.url || '',
          employeeLocationId: handled.data.shift?.employee?.locationId ?? '0',
        })

        dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts: [newShift], view })
        refreshStats(shiftsRows)

        trackAddShiftSubmitEvent({
          view,
          employeeIds: [shift.employeeId],
          shiftsCount: 1,
          restDaysCount: 0,
          shiftType: 'duplicate',
          color: shift.color,
          from: 'drag_and_drop',
        })
      }
    }
  }

  return { copyShift }
}

export default useCopyShift
